// Floating chat bubble (bottom-right), collapsible, with session persistence

import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, RotateCcw } from "lucide-react";
import { Button, Input } from "@cieda/ui";

import { CIEDA_PHONE } from "@/lib/constants";

interface Message {
  readonly role: "user" | "assistant";
  readonly content: string;
}

const API_URL = import.meta.env.VITE_API_URL || "";

/** Get or create a session ID in localStorage */
function getSessionId(): string {
  const KEY = "cieda_chat_session_id";
  let sessionId = localStorage.getItem(KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(KEY, sessionId);
  }
  return sessionId;
}

/** Reset session ID (start fresh conversation) */
function resetSessionId(): string {
  const KEY = "cieda_chat_session_id";
  const newId = crypto.randomUUID();
  localStorage.setItem(KEY, newId);
  return newId;
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<readonly Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(getSessionId);
  const [sessionFull, setSessionFull] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (trimmed.length === 0 || isLoading || sessionFull) return;

    const userMessage: Message = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error("Erreur de connexion");
      }

      const data = await response.json();
      const assistantMessage: Message = {
        role: "assistant",
        content: data.reply,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setSessionFull(data.session_full ?? false);
    } catch {
      const errorMessage: Message = {
        role: "assistant",
        content:
          `Service momentanément indisponible. Contactez-nous au ${CIEDA_PHONE}`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, sessionFull, sessionId]);

  function handleReset() {
    const newId = resetSessionId();
    setSessionId(newId);
    setMessages([]);
    setSessionFull(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <>
      {/* Chat bubble trigger */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[5.5rem] right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-amber-700 text-white shadow-lg hover:bg-amber-800 transition-colors"
          aria-label="Ouvrir le chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-[9rem] sm:right-6 z-[70] flex h-[100dvh] sm:h-[min(500px,70dvh)] w-full sm:w-[380px] sm:max-w-[calc(100vw-2rem)] flex-col sm:rounded-xl border bg-background shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between sm:rounded-t-xl bg-amber-700 px-4 py-3 text-white">
            <div>
              <h3 className="font-semibold font-serif text-sm">Assistant CIEDA</h3>
              <p className="text-xs opacity-80 font-sans">
                Comment puis-je vous aider ?
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleReset}
                className="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                aria-label="Nouvelle conversation"
                title="Nouvelle conversation"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                aria-label="Fermer le chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-8 font-sans">
                <p className="mb-2">Bienvenue !</p>
                <p>
                  Posez-moi une question sur nos produits, nos marques, ou le
                  suivi de votre commande.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm font-sans ${
                    msg.role === "user"
                      ? "bg-amber-700 text-white"
                      : "bg-muted text-foreground"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t p-3">
            {sessionFull ? (
              <div className="text-center text-sm text-muted-foreground font-sans">
                <p className="mb-2">Conversation terminée.</p>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  size="sm"
                  className="font-sans"
                >
                  Nouvelle conversation
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Tapez votre message..."
                  className="flex-1 font-sans"
                  disabled={isLoading}
                  maxLength={500}
                />
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || input.trim().length === 0}
                  size="icon"
                  className="bg-amber-700 hover:bg-amber-800"
                  aria-label="Envoyer"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
